/**
 * SupplyStatusBadge Component
 *
 * Displays supply status relative to demand with colour-coded badges.
 *
 * @module SupplyStatusBadge
 */

import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react'

/**
 * Status configuration
 */
const STATUS_CONFIG = {
  fulfilled: {
    label: 'Supply OK',
    icon: CheckCircle2,
    className: 'bg-green-50 text-green-700 border-green-200'
  },
  partial: {
    label: 'Partial',
    icon: AlertTriangle,
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  shortfall: {
    label: 'Shortfall',
    icon: XCircle,
    className: 'bg-red-50 text-red-700 border-red-200'
  },
  unknown: {
    label: 'Unknown',
    icon: HelpCircle,
    className: 'bg-gray-50 text-gray-500 border-gray-200'
  }
}

/**
 * Format kg value with appropriate precision
 *
 * @param {number} kg - Weight in kg
 * @returns {string} Formatted string
 */
function formatKg(kg) {
  if (kg >= 100) {
    return `${Math.round(kg)}kg`
  }
  return `${kg.toFixed(1)}kg`
}

/**
 * SupplyStatusBadge - Shows supply status for a demand item
 *
 * @param {Object} props
 * @param {'fulfilled' | 'partial' | 'shortfall' | 'unknown'} props.status - Supply status
 * @param {number} props.coveragePercent - Percentage of demand covered
 * @param {number} props.expectedKg - Expected supply in kg
 * @param {number} props.batchCount - Number of production batches
 * @param {string} props.confidence - Confidence level (high/medium/low)
 * @param {boolean} [props.compact=false] - Use compact display
 */
export default function SupplyStatusBadge({
  status,
  coveragePercent,
  expectedKg,
  batchCount,
  confidence,
  compact = false
}) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.unknown
  const Icon = config.icon

  if (compact) {
    return (
      <div
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${config.className}`}
        title={`${coveragePercent}% coverage (${formatKg(expectedKg)} from ${batchCount} batch${batchCount !== 1 ? 'es' : ''})`}
      >
        <Icon className="w-3 h-3" />
        <span>{coveragePercent}%</span>
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${config.className}`}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">{config.label}</span>
          <span className="text-sm">{coveragePercent}%</span>
        </div>
        {batchCount > 0 && (
          <p className="text-xs opacity-75 truncate">
            {formatKg(expectedKg)} from {batchCount} batch{batchCount !== 1 ? 'es' : ''}
            {confidence && confidence !== 'unknown' && ` (${confidence} confidence)`}
          </p>
        )}
        {batchCount === 0 && status === 'shortfall' && (
          <p className="text-xs opacity-75">No production scheduled</p>
        )}
      </div>
    </div>
  )
}
